// src/enums/client-type.ts
import { z } from "zod";
var clientTypeSchema = z.enum(["CLIENT", "SUPPLIER", "BOTH"]);
var ClientType = {
  CLIENT: "CLIENT",
  SUPPLIER: "SUPPLIER",
  BOTH: "BOTH"
};

// src/enums/timesheet-state.ts
import { z as z2 } from "zod";
var timesheetStateSchema = z2.enum([
  "draft",
  "in_approval",
  "approved",
  "rejected"
]);
var TimesheetState = {
  DRAFT: "draft",
  IN_APPROVAL: "in_approval",
  APPROVED: "approved",
  REJECTED: "rejected"
};

// src/enums/user-role.ts
import { z as z3 } from "zod";
var userRoleSchema = z3.enum([
  "OPERATOR",
  "MANAGER",
  "ACCOUNTANT",
  "ADMIN",
  "SUPER_ADMIN"
]);
var UserRole = {
  OPERATOR: "OPERATOR",
  MANAGER: "MANAGER",
  ACCOUNTANT: "ACCOUNTANT",
  ADMIN: "ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN"
};
var APPROVER_ROLES = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.MANAGER
];
var SELF_APPROVING_ROLES = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN
];
var canBeApprover = (role) => APPROVER_ROLES.includes(role);
var isSelfApprovingRole = (role) => SELF_APPROVING_ROLES.includes(role);

// src/enums/user-relationship-type.ts
import { z as z4 } from "zod";
var userRelationshipTypeSchema = z4.enum([
  "INTERNAL_EMPLOYEE",
  "FREELANCER",
  "SUBCONTRACTOR",
  "OTHER"
]);

// src/enums/expense-category.ts
import { z as z5 } from "zod";
var expenseCategorySchema = z5.enum([
  "MEAL",
  "TRANSPORT",
  "ACCOMMODATION",
  "OTHER"
]);
var ExpenseCategory = {
  MEAL: "MEAL",
  TRANSPORT: "TRANSPORT",
  ACCOMMODATION: "ACCOMMODATION",
  OTHER: "OTHER"
};

// src/enums/expense-status.ts
import { z as z6 } from "zod";
var expenseStatusSchema = z6.enum([
  "DRAFT",
  "SUBMITTED",
  "APPROVED",
  "REJECTED"
]);
var ExpenseStatus = {
  DRAFT: "DRAFT",
  SUBMITTED: "SUBMITTED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED"
};

// src/enums/document-category.ts
import { z as z7 } from "zod";
var DOCUMENT_CATEGORIES = ["GENERALE", "BUSTE_PAGA", "CONTRATTI", "AZIENDALI"];
var documentCategorySchema = z7.enum(DOCUMENT_CATEGORIES);
var DocumentCategory = {
  GENERALE: "GENERALE",
  BUSTE_PAGA: "BUSTE_PAGA",
  CONTRATTI: "CONTRATTI",
  AZIENDALI: "AZIENDALI"
};
var PAYSLIP_TYPES = ["MENSILE", "TREDICESIMA", "QUATTORDICESIMA", "CU", "ALTRO"];
var payslipTypeSchema = z7.enum(PAYSLIP_TYPES);
var PayslipType = {
  MENSILE: "MENSILE",
  TREDICESIMA: "TREDICESIMA",
  QUATTORDICESIMA: "QUATTORDICESIMA",
  CU: "CU",
  ALTRO: "ALTRO"
};
var DOCUMENT_CATEGORIES_WITH_TARGET = ["BUSTE_PAGA", "CONTRATTI"];
var documentRequiresTarget = (category) => DOCUMENT_CATEGORIES_WITH_TARGET.includes(category);
var DOCUMENT_MAX_SIZE_BYTES = 15 * 1024 * 1024;
var DOCUMENT_MIME_WHITELIST = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx"
};
var ALL_DOCUMENT_MIMES = Object.keys(DOCUMENT_MIME_WHITELIST);
var DOCUMENT_CATEGORY_MIME = {
  GENERALE: ALL_DOCUMENT_MIMES,
  BUSTE_PAGA: ["application/pdf"],
  CONTRATTI: ["application/pdf"],
  AZIENDALI: ALL_DOCUMENT_MIMES
};
var documentAcceptAttribute = (category) => DOCUMENT_CATEGORY_MIME[category].flatMap((mime) => [`.${DOCUMENT_MIME_WHITELIST[mime]}`, mime]).join(",");
var DOCUMENT_POLICY = {
  GENERALE: {
    view: { OPERATOR: "all", MANAGER: "all", ACCOUNTANT: "all", ADMIN: "all", SUPER_ADMIN: "all" },
    manage: ["ADMIN", "SUPER_ADMIN"]
  },
  BUSTE_PAGA: {
    view: { OPERATOR: "own", MANAGER: "own", ACCOUNTANT: "all", ADMIN: "all", SUPER_ADMIN: "all" },
    manage: ["ACCOUNTANT", "ADMIN", "SUPER_ADMIN"]
  },
  CONTRATTI: {
    view: { OPERATOR: "none", MANAGER: "reports", ACCOUNTANT: "none", ADMIN: "all", SUPER_ADMIN: "all" },
    manage: ["ADMIN", "SUPER_ADMIN"]
  },
  AZIENDALI: {
    view: { OPERATOR: "none", MANAGER: "none", ACCOUNTANT: "all", ADMIN: "all", SUPER_ADMIN: "all" },
    manage: ["ACCOUNTANT", "ADMIN", "SUPER_ADMIN"]
  }
};
var KNOWN_ROLES = ["OPERATOR", "MANAGER", "ACCOUNTANT", "ADMIN", "SUPER_ADMIN"];
var asRole = (role) => role && KNOWN_ROLES.includes(role) ? role : void 0;
var documentViewRule = (role, category) => {
  const known = asRole(role);
  return known ? DOCUMENT_POLICY[category].view[known] : "none";
};
var canViewSomeDocuments = (role, category) => documentViewRule(role, category) !== "none";
var canViewAllDocuments = (role, category) => documentViewRule(role, category) === "all";
var canManageDocuments = (role, category) => {
  const known = asRole(role);
  return known ? DOCUMENT_POLICY[category].manage.includes(known) : false;
};
var visibleDocumentCategories = (role) => DOCUMENT_CATEGORIES.filter((category) => canViewSomeDocuments(role, category));
var manageableDocumentCategories = (role) => DOCUMENT_CATEGORIES.filter((category) => canManageDocuments(role, category));
var DOCUMENT_MANAGE_ROLES = ["ACCOUNTANT", "ADMIN", "SUPER_ADMIN"];

// src/entities/tenant.ts
import { z as z8 } from "zod";
var tenantSchema = z8.object({
  id: z8.number().int().positive(),
  name: z8.string().min(1),
  logoUrl: z8.string().url().nullable().optional(),
  primaryColor: z8.string().regex(/^#[0-9A-Fa-f]{6}$/),
  secondaryColor: z8.string().regex(/^#[0-9A-Fa-f]{6}$/),
  emailFatturazione: z8.string().email(),
  isActive: z8.boolean(),
  language: z8.enum(["en", "it"]),
  companyAddress: z8.string().nullable().optional(),
  vatNumber: z8.string().nullable().optional(),
  pec: z8.string().email().nullable().optional(),
  fiscalCode: z8.string().nullable().optional(),
  bankName: z8.string().nullable().optional(),
  iban: z8.string().nullable().optional(),
  bic: z8.string().nullable().optional(),
  commercialEntityId: z8.number().int().positive().nullable().optional(),
  createdAt: z8.string().datetime(),
  updatedAt: z8.string().datetime()
});
var tenantArraySchema = z8.array(tenantSchema);

// src/entities/user.ts
import { z as z9 } from "zod";
var userSchema = z9.object({
  id: z9.number().int().positive(),
  name: z9.string().min(1),
  email: z9.string().email(),
  codiceFiscale: z9.string().nullable().optional(),
  role: userRoleSchema,
  relationshipType: userRelationshipTypeSchema,
  tenantId: z9.number().int().positive(),
  supplierId: z9.number().int().positive().nullable().optional(),
  approverId: z9.number().int().positive().nullable().optional(),
  isActive: z9.boolean(),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var userArraySchema = z9.array(userSchema);
var userWithPasswordSchema = userSchema.extend({
  password: z9.string()
});

// src/entities/client-supplier.ts
import { z as z10 } from "zod";
var clientSupplierSchema = z10.object({
  id: z10.number().int().positive(),
  name: z10.string().min(1),
  type: clientTypeSchema,
  vatNumber: z10.string().min(1),
  email: z10.string().email(),
  pec: z10.string().email(),
  tenantId: z10.number().int().positive(),
  isActive: z10.boolean(),
  createdAt: z10.string().datetime(),
  updatedAt: z10.string().datetime()
});
var clientSupplierArraySchema = z10.array(clientSupplierSchema);

// src/entities/order.ts
import { z as z11 } from "zod";
var orderSchema = z11.object({
  id: z11.number().int().positive(),
  code: z11.string().min(1),
  description: z11.string().min(1),
  startDate: z11.string(),
  endDate: z11.string(),
  plafond: z11.number().nullable().optional(),
  clientId: z11.number().int().positive(),
  client: clientSupplierSchema.optional(),
  supplierId: z11.number().int().positive().nullable().optional(),
  supplier: clientSupplierSchema.optional(),
  tenantId: z11.number().int().positive(),
  isActive: z11.boolean(),
  createdAt: z11.string().datetime(),
  updatedAt: z11.string().datetime()
});
var orderArraySchema = z11.array(orderSchema);

// src/entities/order-assignment.ts
import { z as z12 } from "zod";
var orderAssignmentSchema = z12.object({
  id: z12.number().int().positive(),
  userId: z12.number().int().positive(),
  orderId: z12.number().int().positive(),
  startDate: z12.string(),
  endDate: z12.string().nullable().optional(),
  costRate: z12.number().nullable().optional(),
  sellRate: z12.number().nullable().optional(),
  tenantId: z12.number().int().positive(),
  isActive: z12.boolean(),
  createdAt: z12.string().datetime(),
  updatedAt: z12.string().datetime()
});
var orderAssignmentArraySchema = z12.array(orderAssignmentSchema);

// src/entities/timesheet.ts
import { z as z13 } from "zod";
var timesheetSchema = z13.object({
  id: z13.number().int().positive(),
  date: z13.string(),
  workedHours: z13.number().min(0).max(24),
  onCallHours: z13.number().min(0).max(24),
  /** On call that day, even with 0 hours (optional for payloads that predate the flag) */
  onCall: z13.boolean().optional(),
  overtimeHours: z13.number().min(0).max(24),
  workPermit: z13.number().min(0).max(24),
  userId: z13.number().int().positive(),
  orderId: z13.number().int().positive().nullable().optional(),
  state: timesheetStateSchema,
  createdAt: z13.string().datetime(),
  updatedAt: z13.string().datetime()
});
var timesheetArraySchema = z13.array(timesheetSchema);

// src/entities/holiday.ts
import { z as z14 } from "zod";
var holidayRuleTypeSchema = z14.enum(["fixed", "floating", "single"]);
var holidayRuleSchema = z14.object({
  id: z14.number().int().positive(),
  tenantId: z14.number().int().positive().nullable(),
  name: z14.string().min(1),
  type: holidayRuleTypeSchema,
  rule: z14.record(z14.string(), z14.unknown()),
  active: z14.boolean(),
  allowTimesheetEntry: z14.boolean(),
  createdBy: z14.number().int().positive().nullable(),
  createdAt: z14.string().datetime(),
  updatedAt: z14.string().datetime()
});
var holidayRuleArraySchema = z14.array(holidayRuleSchema);
var holidayInstanceSchema = z14.object({
  id: z14.number().int().positive(),
  ruleId: z14.number().int().positive().nullable(),
  tenantId: z14.number().int().positive(),
  date: z14.string(),
  name: z14.string().min(1),
  source: z14.enum(["rule", "manual"]),
  overridden: z14.boolean(),
  active: z14.boolean(),
  allowTimesheetEntry: z14.boolean(),
  isCompanyClosing: z14.boolean().default(false),
  endDate: z14.string().nullable().optional(),
  createdAt: z14.string().datetime(),
  updatedAt: z14.string().datetime()
});
var holidayInstanceArraySchema = z14.array(holidayInstanceSchema);
var tenantHolidayRuleSubscriptionSchema = z14.object({
  id: z14.number().int().positive(),
  tenantId: z14.number().int().positive(),
  ruleId: z14.number().int().positive(),
  active: z14.boolean(),
  createdAt: z14.string().datetime()
});

// src/dto/auth.dto.ts
import { z as z15 } from "zod";
var passwordSchema = z15.string().min(8, "Password must be at least 8 characters").regex(/[A-Z]/, "Password must contain at least one uppercase letter").regex(/[a-z]/, "Password must contain at least one lowercase letter").regex(/[0-9]/, "Password must contain at least one number").regex(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character");
var loginSchema = z15.object({
  email: z15.string().email(),
  password: z15.string().min(1)
  // Login doesn't validate complexity, only registration does
});
var registerTenantSchema = z15.object({
  name: z15.string().min(1),
  logoUrl: z15.string().url().optional(),
  primaryColor: z15.string().regex(/^#[0-9A-Fa-f]{6}$/),
  secondaryColor: z15.string().regex(/^#[0-9A-Fa-f]{6}$/),
  emailFatturazione: z15.string().email(),
  companyAddress: z15.string().optional(),
  vatNumber: z15.string().optional(),
  pec: z15.string().email().optional(),
  autoCreateCommercialEntity: z15.boolean().optional(),
  commercialEntityType: clientTypeSchema.optional()
});
var registerUserSchema = z15.object({
  name: z15.string().min(1),
  email: z15.string().email(),
  password: passwordSchema,
  tenantId: z15.number().int().positive()
});

// src/dto/tenant.dto.ts
import { z as z16 } from "zod";
var createTenantSchema = z16.object({
  name: z16.string().min(1),
  logoUrl: z16.string().url().optional(),
  primaryColor: z16.string().regex(/^#[0-9A-Fa-f]{6}$/),
  secondaryColor: z16.string().regex(/^#[0-9A-Fa-f]{6}$/),
  emailFatturazione: z16.string().email(),
  language: z16.enum(["en", "it"]).optional(),
  companyAddress: z16.string().optional(),
  vatNumber: z16.string().optional(),
  pec: z16.string().email().optional(),
  fiscalCode: z16.string().optional(),
  bankName: z16.string().optional(),
  iban: z16.string().optional(),
  bic: z16.string().optional(),
  autoCreateCommercialEntity: z16.boolean().optional(),
  commercialEntityType: clientTypeSchema.optional()
});
var updateTenantSchema = createTenantSchema.partial();

// src/dto/user.dto.ts
import { z as z17 } from "zod";
var codiceFiscaleSchema = z17.string().trim().toUpperCase().regex(/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/, "Invalid codice fiscale").nullable().optional();
var approverIdSchema = z17.number().int().positive().nullable().optional();
var validateRelationshipSupplier = (value, ctx) => {
  const hasSupplier = typeof value.supplierId === "number" && value.supplierId > 0;
  if (!hasSupplier) {
    ctx.addIssue({
      code: z17.ZodIssueCode.custom,
      path: ["supplierId"],
      message: "supplierId is required for all users"
    });
  }
};
var createUserSchema = z17.object({
  name: z17.string().min(1),
  email: z17.string().email(),
  codiceFiscale: codiceFiscaleSchema,
  password: passwordSchema,
  role: userRoleSchema.optional(),
  relationshipType: userRelationshipTypeSchema.default("INTERNAL_EMPLOYEE"),
  supplierId: z17.number().int().positive().nullable().optional(),
  approverId: approverIdSchema
}).superRefine(validateRelationshipSupplier);
var inviteUserSchema = z17.object({
  name: z17.string().min(1),
  email: z17.string().email(),
  codiceFiscale: codiceFiscaleSchema,
  role: userRoleSchema.optional(),
  relationshipType: userRelationshipTypeSchema.default("INTERNAL_EMPLOYEE"),
  supplierId: z17.number().int().positive().nullable().optional(),
  approverId: approverIdSchema
}).superRefine(validateRelationshipSupplier);
var updateUserBaseSchema = z17.object({
  name: z17.string().min(1).optional(),
  email: z17.string().email().optional(),
  codiceFiscale: codiceFiscaleSchema,
  password: passwordSchema.optional(),
  role: userRoleSchema.optional(),
  relationshipType: userRelationshipTypeSchema.optional(),
  supplierId: z17.number().int().positive().nullable().optional(),
  approverId: approverIdSchema
});
var updateUserSchema = updateUserBaseSchema.superRefine(validateRelationshipSupplier);

// src/dto/client-supplier.dto.ts
import { z as z18 } from "zod";
var createClientSupplierSchema = z18.object({
  name: z18.string().min(1),
  type: clientTypeSchema,
  vatNumber: z18.string().min(1),
  email: z18.string().email("Invalid email format"),
  pec: z18.string().email("Invalid PEC format"),
  address: z18.string().optional(),
  autoCreateTenant: z18.boolean().optional(),
  linkedTenantId: z18.number().int().positive().optional(),
  tenantId: z18.number().int().positive().optional()
});
var updateClientSupplierSchema = createClientSupplierSchema.partial().refine(
  (data) => {
    return data.name !== void 0 || data.email !== void 0 || data.pec !== void 0;
  },
  { message: "At least one field must be updated" }
);

// src/dto/order.dto.ts
import { z as z19 } from "zod";
var createOrderSchema = z19.object({
  code: z19.string().min(1),
  description: z19.string().min(1),
  startDate: z19.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z19.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  plafond: z19.number().nonnegative().optional(),
  clientId: z19.number().int().positive(),
  supplierId: z19.number().int().positive().nullable().optional(),
  tenantId: z19.number().int().positive().optional()
});
var updateOrderSchema = createOrderSchema.partial();

// src/dto/order-assignment.dto.ts
import { z as z20 } from "zod";
var createOrderAssignmentSchema = z20.object({
  userId: z20.number().int().positive(),
  orderId: z20.number().int().positive(),
  startDate: z20.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z20.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  costRate: z20.coerce.number().nonnegative().optional(),
  sellRate: z20.coerce.number().nonnegative().optional()
});
var updateOrderAssignmentSchema = createOrderAssignmentSchema.partial();
var splitOrderAssignmentSchema = z20.object({
  splitDate: z20.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  costRate: z20.coerce.number().nonnegative().optional(),
  sellRate: z20.coerce.number().nonnegative().optional()
});

// src/dto/timesheet.dto.ts
import { z as z21 } from "zod";
var createTimesheetSchema = z21.object({
  date: z21.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  orderId: z21.number().int().positive(),
  workedHours: z21.number().min(0).max(24),
  onCallHours: z21.number().min(0).max(24),
  /** On call that day, even with 0 hours; omitted = on call when onCallHours > 0 */
  onCall: z21.boolean().optional(),
  overtimeHours: z21.number().min(0).max(24).optional().default(0),
  workPermit: z21.number().min(0).max(24)
});
var updateTimesheetSchema = z21.object({
  workedHours: z21.number().min(0).max(24).optional(),
  onCallHours: z21.number().min(0).max(24).optional(),
  onCall: z21.boolean().optional(),
  overtimeHours: z21.number().min(0).max(24).optional(),
  workPermit: z21.number().min(0).max(24).optional()
});
var timesheetEntrySchema = z21.object({
  date: z21.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  orderId: z21.number().int().positive(),
  workedHours: z21.number().min(0).max(24),
  onCallHours: z21.number().min(0).max(24),
  /** On call that day, even with 0 hours; omitted = on call when onCallHours > 0 */
  onCall: z21.boolean().optional(),
  overtimeHours: z21.number().min(0).max(24).optional().default(0),
  workPermit: z21.number().min(0).max(24).default(0)
});
var batchSaveTimesheetSchema = z21.object({
  entries: z21.array(timesheetEntrySchema)
});
var updateTimesheetStateSchema = z21.object({
  state: timesheetStateSchema
});

// src/dto/holiday.dto.ts
import { z as z22 } from "zod";
var fixedRuleSchema = z22.object({
  month: z22.number().int().min(1).max(12),
  day: z22.number().int().min(1).max(31)
});
var floatingRuleSchema = z22.object({
  month: z22.number().int().min(1).max(12),
  weekday: z22.number().int().min(0).max(6),
  occurrence: z22.union([
    z22.literal(1),
    z22.literal(2),
    z22.literal(3),
    z22.literal(4),
    z22.literal(5),
    z22.literal(-1)
  ])
});
var singleRuleSchema = z22.union([
  z22.object({
    date: z22.string().regex(/^\d{4}-\d{2}-\d{2}$/)
  }),
  z22.object({
    startDate: z22.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    endDate: z22.string().regex(/^\d{4}-\d{2}-\d{2}$/)
  }).refine((value) => value.startDate <= value.endDate, {
    message: "startDate must be before or equal to endDate",
    path: ["endDate"]
  })
]);
var createHolidayRuleSchema = z22.object({
  name: z22.string().min(1),
  type: z22.enum(["fixed", "floating", "single"]),
  rule: z22.record(z22.string(), z22.unknown()),
  allowTimesheetEntry: z22.boolean().optional()
});
var updateHolidayRuleSchema = z22.object({
  name: z22.string().min(1).optional(),
  active: z22.boolean().optional(),
  rule: z22.record(z22.string(), z22.unknown()).optional(),
  allowTimesheetEntry: z22.boolean().optional()
});
var generateHolidayInstancesSchema = z22.object({
  year: z22.coerce.number().int().min(2e3).max(2100),
  tenantId: z22.coerce.number().int().positive().optional()
});
var updateHolidayInstanceSchema = z22.object({
  name: z22.string().min(1).optional(),
  active: z22.boolean().optional(),
  isCompanyClosing: z22.boolean().optional(),
  date: z22.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z22.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
}).refine(
  (value) => {
    if (value.isCompanyClosing && !value.endDate) {
      return false;
    }
    return true;
  },
  { message: "endDate is required when isCompanyClosing is true", path: ["endDate"] }
);

// src/dto/expense-claim.dto.ts
import { z as z23 } from "zod";
var attachmentSchema = z23.object({
  // Upload endpoint returns an internal storage fileId (not always a public URL).
  fileUrl: z23.string().trim().min(1),
  mimeType: z23.string().min(1)
});
var createExpenseClaimSchema = z23.object({
  date: z23.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  category: expenseCategorySchema,
  amount: z23.number().positive(),
  hasReceipt: z23.boolean(),
  declaredWithoutReceipt: z23.boolean().default(false),
  notes: z23.string().trim().max(2e3).optional(),
  attachments: z23.array(attachmentSchema).optional().default([])
}).superRefine((value, ctx) => {
  if (!value.hasReceipt && !value.declaredWithoutReceipt) {
    ctx.addIssue({
      code: z23.ZodIssueCode.custom,
      path: ["declaredWithoutReceipt"],
      message: "declaredWithoutReceipt must be true when hasReceipt is false"
    });
  }
});
var updateExpenseClaimBaseSchema = z23.object({
  date: z23.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  category: expenseCategorySchema.optional(),
  amount: z23.number().positive().optional(),
  hasReceipt: z23.boolean().optional(),
  declaredWithoutReceipt: z23.boolean().optional(),
  notes: z23.string().trim().max(2e3).optional(),
  attachments: z23.array(attachmentSchema).optional()
});
var updateExpenseClaimSchema = updateExpenseClaimBaseSchema.superRefine((value, ctx) => {
  if (value.hasReceipt === false && value.declaredWithoutReceipt !== true) {
    ctx.addIssue({
      code: z23.ZodIssueCode.custom,
      path: ["declaredWithoutReceipt"],
      message: "declaredWithoutReceipt must be true when hasReceipt is false"
    });
  }
});
var updateExpenseClaimStatusSchema = z23.object({
  status: expenseStatusSchema,
  reviewNote: z23.string().trim().max(2e3).optional()
}).refine((value) => value.status === "APPROVED" || value.status === "REJECTED", {
  message: "status must be APPROVED or REJECTED",
  path: ["status"]
});

// src/dto/timesheet-calendar.dto.ts
import { z as z24 } from "zod";
var calendarLeaveSchema = z24.object({
  type: z24.string(),
  durationType: z24.string(),
  hours: z24.number().min(0).optional(),
  /** Only an approved request is time off; a pending one is shown on the day but counts for nothing. */
  state: z24.enum(["pending", "approved"]).optional()
});
var monthlyCalendarEntrySchema = z24.object({
  date: z24.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timesheets: z24.array(timesheetSchema),
  leave: calendarLeaveSchema.optional()
});
var monthlyCalendarSchema = z24.array(monthlyCalendarEntrySchema);

// src/dto/document.dto.ts
import { z as z25 } from "zod";
var NO_CONTROL_CHARS = /^[^\u0000-\u001F\u007F]*$/;
var issue = (ctx, path, message) => ctx.addIssue({ code: z25.ZodIssueCode.custom, path: [path], message });
var optionalFormBoolean = z25.preprocess(
  (value) => value === void 0 || value === "" ? void 0 : value === "true" || value === true,
  z25.boolean().optional()
);
var createDocumentSchema = z25.object({
  category: documentCategorySchema,
  title: z25.string().trim().min(1).max(200).regex(NO_CONTROL_CHARS).optional(),
  description: z25.string().trim().max(2e3).optional(),
  targetUserId: z25.coerce.number().int().positive().optional(),
  payslipType: payslipTypeSchema.optional(),
  payslipYear: z25.coerce.number().int().min(2e3).optional(),
  payslipMonth: z25.coerce.number().int().min(1).max(12).optional(),
  notify: optionalFormBoolean,
  notifyByEmail: optionalFormBoolean
}).superRefine((value, ctx) => {
  const needsTarget = documentRequiresTarget(value.category);
  if (needsTarget && value.targetUserId === void 0) issue(ctx, "targetUserId", "REQUIRED");
  if (!needsTarget && value.targetUserId !== void 0) issue(ctx, "targetUserId", "FORBIDDEN");
  if (value.category === "BUSTE_PAGA") {
    const type = value.payslipType ?? "MENSILE";
    if (value.payslipYear === void 0) issue(ctx, "payslipYear", "REQUIRED");
    else if (value.payslipYear > (/* @__PURE__ */ new Date()).getFullYear() + 1) issue(ctx, "payslipYear", "TOO_FAR_IN_FUTURE");
    if (type !== "CU" && value.payslipMonth === void 0) issue(ctx, "payslipMonth", "REQUIRED");
    if (type === "CU" && value.payslipMonth !== void 0) issue(ctx, "payslipMonth", "FORBIDDEN_FOR_CU");
  } else {
    if (value.payslipYear !== void 0 || value.payslipMonth !== void 0 || value.payslipType !== void 0) {
      issue(ctx, "payslipYear", "FORBIDDEN");
    }
    if (value.title === void 0) issue(ctx, "title", "REQUIRED");
  }
  if (value.category !== "GENERALE" && value.notifyByEmail) issue(ctx, "notifyByEmail", "FORBIDDEN");
});
var documentConflictStrategySchema = z25.enum(["replace", "keep"]);
var createDocumentQuerySchema = z25.object({
  onConflict: documentConflictStrategySchema.optional()
});
var updateDocumentSchema = z25.object({
  title: z25.string().trim().min(1).max(200).regex(NO_CONTROL_CHARS).optional(),
  description: z25.string().trim().max(2e3).nullable().optional()
}).refine((value) => value.title !== void 0 || value.description !== void 0, {
  message: "AT_LEAST_ONE_FIELD",
  path: ["title"]
});
var listDocumentsQuerySchema = z25.object({
  category: documentCategorySchema,
  userId: z25.coerce.number().int().positive().optional(),
  year: z25.coerce.number().int().min(2e3).max(2100).optional(),
  month: z25.coerce.number().int().min(1).max(12).optional(),
  payslipType: payslipTypeSchema.optional(),
  onlyMine: z25.enum(["0", "1"]).optional(),
  search: z25.string().trim().max(100).optional()
});

// src/responses/dashboard.ts
import { z as z26 } from "zod";
var orderStatsSchema = z26.object({
  orderId: z26.number(),
  orderCode: z26.string(),
  totalDays: z26.number(),
  totalHours: z26.number().optional(),
  netProfit: z26.number().optional(),
  billingRate: z26.number().optional(),
  costRate: z26.number().optional()
});
var hoursByProjectSchema = z26.object({
  orderId: z26.number(),
  orderCode: z26.string(),
  orderDescription: z26.string().optional(),
  hours: z26.number()
});
var approvalBreakdownSchema = z26.object({
  draft: z26.number(),
  in_approval: z26.number(),
  approved: z26.number(),
  rejected: z26.number()
});
var monthlyTotalSchema = z26.object({
  month: z26.string(),
  // "2026-01"
  hours: z26.number(),
  days: z26.number(),
  overtime: z26.number(),
  onCall: z26.number(),
  approvedCount: z26.number(),
  pendingCount: z26.number()
});
var hoursByUserSchema = z26.object({
  userId: z26.number(),
  userName: z26.string(),
  hours: z26.number()
});
var teamStatsSchema = z26.object({
  activeUsers: z26.number(),
  totalNetProfit: z26.number(),
  hoursByUser: z26.array(hoursByUserSchema)
});
var dashboardStatsSchema = z26.object({
  totalWorkingHours: z26.number(),
  totalWorkingDays: z26.number(),
  totalOnCallHours: z26.number(),
  totalOvertimeHours: z26.number(),
  totalWorkPermitHours: z26.number(),
  totalNetProfit: z26.number().optional(),
  orderStats: z26.array(orderStatsSchema),
  // New enhanced fields
  hoursByProject: z26.array(hoursByProjectSchema).optional(),
  approvalBreakdown: approvalBreakdownSchema.optional(),
  monthlyTotals: z26.array(monthlyTotalSchema).optional(),
  teamStats: teamStatsSchema.optional()
});

// src/responses/auth.ts
import { z as z27 } from "zod";
var loginResponseSchema = z27.object({
  access_token: z27.string(),
  user: userSchema,
  tenant: tenantSchema.optional()
});
var registerResponseSchema = z27.object({
  user: userSchema,
  tenant: tenantSchema
});

// src/responses/paginated.ts
import { z as z28 } from "zod";
var paginationMetaSchema = z28.object({
  total: z28.number(),
  page: z28.number(),
  limit: z28.number(),
  totalPages: z28.number()
});
function createPaginatedSchema(itemSchema) {
  return z28.object({
    data: z28.array(itemSchema),
    meta: paginationMetaSchema
  });
}
export {
  APPROVER_ROLES,
  ClientType,
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORIES_WITH_TARGET,
  DOCUMENT_CATEGORY_MIME,
  DOCUMENT_MANAGE_ROLES,
  DOCUMENT_MAX_SIZE_BYTES,
  DOCUMENT_MIME_WHITELIST,
  DOCUMENT_POLICY,
  DocumentCategory,
  ExpenseCategory,
  ExpenseStatus,
  PAYSLIP_TYPES,
  PayslipType,
  SELF_APPROVING_ROLES,
  TimesheetState,
  UserRole,
  approvalBreakdownSchema,
  batchSaveTimesheetSchema,
  calendarLeaveSchema,
  canBeApprover,
  canManageDocuments,
  canViewAllDocuments,
  canViewSomeDocuments,
  clientSupplierArraySchema,
  clientSupplierSchema,
  clientTypeSchema,
  createClientSupplierSchema,
  createDocumentQuerySchema,
  createDocumentSchema,
  createExpenseClaimSchema,
  createHolidayRuleSchema,
  createOrderAssignmentSchema,
  createOrderSchema,
  createPaginatedSchema,
  createTenantSchema,
  createTimesheetSchema,
  createUserSchema,
  dashboardStatsSchema,
  documentAcceptAttribute,
  documentCategorySchema,
  documentConflictStrategySchema,
  documentRequiresTarget,
  documentViewRule,
  expenseCategorySchema,
  expenseStatusSchema,
  generateHolidayInstancesSchema,
  holidayInstanceArraySchema,
  holidayInstanceSchema,
  holidayRuleArraySchema,
  holidayRuleSchema,
  holidayRuleTypeSchema,
  hoursByProjectSchema,
  hoursByUserSchema,
  inviteUserSchema,
  isSelfApprovingRole,
  listDocumentsQuerySchema,
  loginResponseSchema,
  loginSchema,
  manageableDocumentCategories,
  monthlyCalendarEntrySchema,
  monthlyCalendarSchema,
  monthlyTotalSchema,
  orderArraySchema,
  orderAssignmentArraySchema,
  orderAssignmentSchema,
  orderSchema,
  orderStatsSchema,
  paginationMetaSchema,
  passwordSchema,
  payslipTypeSchema,
  registerResponseSchema,
  registerTenantSchema,
  registerUserSchema,
  splitOrderAssignmentSchema,
  teamStatsSchema,
  tenantArraySchema,
  tenantHolidayRuleSubscriptionSchema,
  tenantSchema,
  timesheetArraySchema,
  timesheetEntrySchema,
  timesheetSchema,
  timesheetStateSchema,
  updateClientSupplierSchema,
  updateDocumentSchema,
  updateExpenseClaimSchema,
  updateExpenseClaimStatusSchema,
  updateHolidayInstanceSchema,
  updateHolidayRuleSchema,
  updateOrderAssignmentSchema,
  updateOrderSchema,
  updateTenantSchema,
  updateTimesheetSchema,
  updateTimesheetStateSchema,
  updateUserSchema,
  userArraySchema,
  userRelationshipTypeSchema,
  userRoleSchema,
  userSchema,
  userWithPasswordSchema,
  visibleDocumentCategories
};
